import { Theme } from '@mui/material';
import {
  AgeGroup,
  GetTimetableStructureResponse,
  PeriodTypeEnum,
  SchoolLevel,
  StringOrNull,
  WeekdayEnum,
} from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { getSelectedItemsWithGrouping } from '@schooly/components/filters';
import { format, setDay } from 'date-fns';
import { useCallback } from 'react';
import { timeToMinutes } from './time-utils';

type GetAgeGroupNamesParams = {
  ageGroupIds: string[];
  getAgeGroupById: (id: string) => AgeGroup | undefined;
  getSchoolLevelById: (id: string) => SchoolLevel | undefined;
  schoolLevelsWithAgeGroupsMap: Record<string, AgeGroup[]>;
};

export const getAgeGroupNames = ({
  ageGroupIds,
  getAgeGroupById,
  getSchoolLevelById,
  schoolLevelsWithAgeGroupsMap,
}: GetAgeGroupNamesParams): string => {
  const items = ageGroupIds.reduce<{ id: string; groupId: StringOrNull }[]>((acc, id) => {
    const ageGroup = getAgeGroupById(id);
    return ageGroup ? [...acc, { id: ageGroup.id, groupId: ageGroup.level_id }] : acc;
  }, []);

  const itemsWithGrouping = getSelectedItemsWithGrouping(items, schoolLevelsWithAgeGroupsMap);

  return itemsWithGrouping
    .map((item) =>
      item.isGroup ? getSchoolLevelById(item.id)?.name || '' : getAgeGroupById(item.id)?.name || '',
    )
    .filter(Boolean)
    .join(', ');
};

export const WEEKDAY_TO_DAY_INDEX: Record<WeekdayEnum, number> = {
  [WeekdayEnum.Monday]: 1,
  [WeekdayEnum.Tuesday]: 2,
  [WeekdayEnum.Wednesday]: 3,
  [WeekdayEnum.Thursday]: 4,
  [WeekdayEnum.Friday]: 5,
  [WeekdayEnum.Saturday]: 6,
  [WeekdayEnum.Sunday]: 0,
};

export const getWeekdayLabel = (weekday: WeekdayEnum, formatString: string): string => {
  const dayIndex = WEEKDAY_TO_DAY_INDEX[weekday];
  const date = setDay(new Date(), dayIndex);
  return format(date, formatString);
};

const WEEKDAY_ORDER = [
  WeekdayEnum.Monday,
  WeekdayEnum.Tuesday,
  WeekdayEnum.Wednesday,
  WeekdayEnum.Thursday,
  WeekdayEnum.Friday,
  WeekdayEnum.Saturday,
  WeekdayEnum.Sunday,
];

export const sortWeekdays = (weekdays: WeekdayEnum[]): WeekdayEnum[] => {
  return WEEKDAY_ORDER.filter((day) => weekdays.includes(day));
};

type Duration = { hours: number; minutes: number };

export { timeToMinutes } from './time-utils';

export const getMinutesDuration = (startTime: string, endTime: string): number => {
  return timeToMinutes(endTime) - timeToMinutes(startTime);
};

export const minutesToTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const getDurationFromMinutes = (minutes: number): Duration => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return { hours, minutes: remainingMinutes };
};

export const getDurationLabel = (
  duration: Duration,
  labels: { hours: string; minutes: string },
): string => {
  const { hours, minutes } = duration;
  const { hours: hoursLabelText, minutes: minutesLabelText } = labels;

  if (hours === 0 && minutes === 0) {
    return `0 ${minutesLabelText}`;
  }

  const hoursLabel = hours > 0 ? `${hours} ${hoursLabelText}` : '';
  const minutesLabel = minutes > 0 ? `${minutes} ${minutesLabelText}` : '';

  return [hoursLabel, minutesLabel].filter(Boolean).join(' ');
};

export const getPeriodTypePalette = (
  periodType: PeriodTypeEnum,
  theme: Theme,
): { primary: string; secondary: string } => {
  switch (periodType) {
    case PeriodTypeEnum.Lesson:
      return {
        primary: theme.palette.primary.main,
        secondary: theme.palette.common.main5,
      };
    case PeriodTypeEnum.Break:
      return {
        primary: theme.palette.common.green1,
        secondary: theme.palette.common.green4,
      };
    case PeriodTypeEnum.Activity:
      return {
        primary: theme.palette.common.moss1,
        secondary: theme.palette.common.moss4,
      };
    case PeriodTypeEnum.Registration:
      return {
        primary: theme.palette.common.dijon1,
        secondary: theme.palette.common.dijon4,
      };
  }
};

export const hasTimeGap = (endTime: string, nextStartTime: string): boolean => {
  return timeToMinutes(endTime) < timeToMinutes(nextStartTime);
};

export const hasTimeOverlap = (endTime: string, nextStartTime: string): boolean => {
  return timeToMinutes(endTime) > timeToMinutes(nextStartTime);
};

export type StructureChange =
  | { type: 'remove_age_group'; ids: string[] }
  | { type: 'remove_weekday'; day: WeekdayEnum }
  | { type: 'remove_period'; id: string }
  | { type: 'change_period_type'; id: string; newType: PeriodTypeEnum }
  | { type: 'remove_slot'; id: StringOrNull };

export const useTimetableStructureConfirmation = () => {
  const { getConfirmation } = useConfirmationDialog();

  const confirmChange = useCallback(
    async (structure: GetTimetableStructureResponse, change: StructureChange): Promise<boolean> => {
      let needsConfirmation = false;

      const { age_group_ids, weekdays, periods, time_slots } = structure;

      switch (change.type) {
        case 'remove_age_group':
          needsConfirmation = change.ids.some((id) => age_group_ids.includes(id));
          break;
        case 'remove_weekday':
          needsConfirmation = weekdays.includes(change.day);
          break;
        case 'remove_period':
          needsConfirmation = periods.some((p) => p.id === change.id);
          break;
        case 'change_period_type': {
          const initialPeriod = periods.find((p) => p.id === change.id);
          needsConfirmation = initialPeriod ? change.newType !== initialPeriod.type : false;
          break;
        }
        case 'remove_slot':
          needsConfirmation = change.id ? time_slots.some((s) => s.id === change.id) : false;
          break;
      }

      if (!needsConfirmation) return true;

      const result = await getConfirmation({
        textId: 'school-timetableStructures-UpdateConfirmation',
      });

      return !!result;
    },
    [getConfirmation],
  );

  return { confirmChange };
};
